import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { AuthenticationService } from '../../../services/authentication.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { BrandsService } from '../brands.service';
import { Brand } from '../brands.model';
import { Page } from '../../../models/page-wrapper.model';
import * as XLSX from 'xlsx';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, forkJoin, of, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ReactiveFormsModule } from '@angular/forms';
import { EntityDropdownPickerComponent } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { Business } from '../../business/business.model';
import { BusinessService } from '../../business/business.service';
import { BusinessUnit } from '../../business-units/business-unit.model';
import { BusinessUnitsService } from '../../business-units/business-units.service';
@Component({
  selector: 'app-brands-list',
  templateUrl: './brands-list.component.html',
  styleUrl: './brands-list.component.css',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    RouterModule,
    InputGroupModule,
    InputGroupAddonModule,
    ReactiveFormsModule,
    InputTextModule,
    IftaLabelModule,
    EntityDropdownPickerComponent,

  ],
  providers: [ConfirmationService, MessageService],
})
export class BrandsListComponent implements OnInit, OnDestroy {
  @ViewChild('dt') dt!: Table;
  filterForm!: FormGroup;
  private filterSub!: Subscription;

  brands: Brand[] = [];
  totalRecords = 0;
  isLoading = false;
  canCreateBrand = false;
  canUpdateBrand = false;
  canDeleteBrand = false;
  businessPickerAdapter!: EntityDropdownPickerAdapter<Business>;
  businessUnitPickerAdapter!: EntityDropdownPickerAdapter<BusinessUnit>;
  private authSubscription: Subscription | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private brandsService: BrandsService,
    private businessService: BusinessService,
    private businessUnitsService: BusinessUnitsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private authService: AuthenticationService,
    private authorizationService: AuthorizationService
  ) {}


  ngOnInit(): void {
    this.buildFilterForm();
    this.businessPickerAdapter = this.businessService.createBusinessPickerAdapter();
    this.businessUnitPickerAdapter = this.businessUnitsService.createBusinessUnitPickerAdapter();
    this.filterSub = this.filterForm.valueChanges
      .pipe(
        debounceTime(500), 
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.dt.reset();
      });

    this.loadBrands();
    this.checkPermissions();
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.filterSub?.unsubscribe();
  }

  buildFilterForm() {
    this.filterForm = this.fb.group({
      brandName: [''],
      brandCode: [''],
      business: [null],
      businessUnit: [null],
    });
  }

  onSearch(): void {
    this.dt.reset();
  }

  onReset(): void {
    this.filterForm.reset({
      brandName: '',
      brandCode: '',
      business: null,
      businessUnit: null,
    });
    this.dt.reset();
  }

  private checkPermissions(): void {
    this.authSubscription = this.authService.currentUser$.subscribe((user) => {
      if (user) {
        this.canCreateBrand = this.authorizationService.hasPermission(
          user,
          'Brand',
          ['create']
        );
        this.canUpdateBrand = this.authorizationService.hasPermission(
          user,
          'Brand',
          ['update']
        );
        this.canDeleteBrand = this.authorizationService.hasPermission(
          user,
          'Brand',
          ['delete']
        );
      } else {
        this.canCreateBrand = false;
        this.canUpdateBrand = false;
        this.canDeleteBrand = false;
      }
    });
  }

  loadBrands(
    event?: TableLazyLoadEvent,
    isExport = false,
    callback?: (brands: Brand[]) => void
  ): void {
    this.isLoading = true;
    const size = isExport ? 2000 : event?.rows ?? 10;
    const page = isExport ? 0 : (event?.first ? Math.floor(event.first / size) : 0);
    const params: any = {
      page,
      size,
      brandName: this.filterForm.value.brandName,
      brandCode: this.filterForm.value.brandCode,
      businessId: this.filterForm.value.business?.id,
      businessUnitId: this.filterForm.value.businessUnit?.id,
    };

    Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key];
      }
    });

    this.brandsService.getAllBrands(params).pipe(
      map((page: Page<Brand>) => ({
        page,
        brands: page.content || [],
      })),
      catchError(() => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Brands.',
        });
        this.brands = [];
        this.totalRecords = 0;
        this.isLoading = false;
        return of({ page: undefined, brands: [] as Brand[] });
      }),
      switchMap(({ page, brands }) => this.enrichBrands(brands).pipe(
        map((enrichedBrands) => ({ page, brands: enrichedBrands }))
      ))
    ).subscribe({
      next: ({ page, brands }) => {
        if (page) {
          this.brands = brands;
          this.totalRecords = page.totalElements;
        }
        this.isLoading = false;
        if (callback) {
          callback(brands);
        }
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  deleteBrand(id: number): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this Brand?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',

      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',

      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-success',

      accept: () => {
        this.brandsService.deleteBrand(id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Brand Deleted successfully.`,
            });
            setTimeout(() => {
              this.loadBrands();
            }, 500);
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: `Failed to Delete brand.`,
            });
            console.error(err);
          },
        });
      },
    });
  }

  createBrand() {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['brand', 'create'] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }

  editBrand(id: number): void {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['brand', 'edit', id] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }

  exportExcel(): void {
    this.loadBrands(undefined, true, (brands: Brand[]) => {
      const dataToExport = brands.map((brand) => ({
        'Brand Name': brand.brandName,
        'Brand Code': brand.brandCode,
        Business: brand.businessObject?.businessName ?? brand.businessId ?? null,
        'Business Unit': brand.businessUnitObject?.businessUnitName ?? brand.businessUnitId ?? null,
      }));

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Brands');
      XLSX.writeFile(wb, 'Brands_Export.xlsx');
    });
  }

  private enrichBrands(brands: Brand[]) {
    const businessIds = [...new Set(brands.map((brand) => brand.businessId).filter((id): id is number => !!id))];
    const businessUnitIds = [...new Set(brands.map((brand) => brand.businessUnitId).filter((id): id is number => !!id))];

    return forkJoin({
      businesses: businessIds.length
        ? this.businessService.getAllBusinesss({ page: 0, size: 5000 }).pipe(
            map((page) => page.content.filter((business) => businessIds.includes(business.id))),
            catchError(() => of([] as Business[]))
          )
        : of([] as Business[]),
      businessUnits: businessUnitIds.length
        ? this.businessUnitsService.getAllBusinessUnits({ page: 0, size: 5000 }).pipe(
            map((page) => page.content.filter((businessUnit) => businessUnitIds.includes(businessUnit.id))),
            catchError(() => of([] as BusinessUnit[]))
          )
        : of([] as BusinessUnit[]),
    }).pipe(
      map(({ businesses, businessUnits }) => {
        const businessMap = new Map(businesses.map((business) => [business.id, business]));
        const businessUnitMap = new Map(businessUnits.map((businessUnit) => [businessUnit.id, businessUnit]));

        return brands.map((brand) => ({
          ...brand,
          businessObject: businessMap.get(brand.businessId),
          businessUnitObject: brand.businessUnitId ? businessUnitMap.get(brand.businessUnitId) : undefined,
        }));
      })
    );
  }
}
